import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, User, BookOpen, Upload, Settings, Users, BarChart3, Library, Trophy, MessageCircle, Banknote } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import NotificationBell from './NotificationBell';
import UserAvatar from './avatars/UserAvatar';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [avatarTick, setAvatarTick] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = localStorage.getItem('token');
  const userName = localStorage.getItem('userName') || 'User';
  const userRole = localStorage.getItem('userRole') || 'member';

  // Admin-only: poll the pending-approvals count so new registrations are
  // never missed. Cheap indexed count, refreshed every 60s.
  useEffect(() => {
    if (userRole !== 'admin' || !isAuthenticated) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pending-approvals-count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setPendingApprovals(d.count || 0);
      } catch { /* badge is best-effort */ }
    };
    fetchCount();
    const intv = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(intv); };
  }, [userRole, isAuthenticated]);
  // Re-read on every tick so the navbar avatar refreshes the moment the user
  // saves a new selection in /settings (which writes to localStorage).
  const _ = avatarTick; // eslint-disable-line no-unused-vars
  const avatarId = localStorage.getItem('userAvatarId') || localStorage.getItem('avatar_id') || null;
  const avatarPhotoUrl = localStorage.getItem('userAvatarPhotoUrl') || localStorage.getItem('avatar_photo_url') || null;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    const handleStorage = (e) => {
      if (!e || !e.key || ['userAvatarId', 'userAvatarPhotoUrl', 'avatar_id', 'avatar_photo_url', 'userName'].includes(e.key)) {
        setAvatarTick((t) => t + 1);
      }
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('storage', handleStorage);
    // Same-tab updates: re-poll every 5s (cheap — just localStorage reads)
    const intv = setInterval(() => setAvatarTick((t) => t + 1), 5000);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorage);
      clearInterval(intv);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  /**
   * Home click handler that ALWAYS resets the home page to its default
   * state — even when the user is already on `/`. React Router's <Link>
   * to the same path is a no-op, so we:
   *   1. navigate('/') to ensure the route is correct
   *   2. dispatch a 'scb:home-reset' window event that LandingPage listens
   *      for to clear its search/filter state
   *   3. scroll the window back to the top with a smooth animation
   */
  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
    }
    window.dispatchEvent(new CustomEvent('scb:home-reset'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isAuthenticated && (location.pathname === '/login' || location.pathname === '/signup')) {
    return null;
  }

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 pointer-events-none ${
        scrolled ? 'bg-black' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div className="flex items-center space-x-8 pointer-events-auto">
            <Link to="/" onClick={handleHomeClick} className="flex items-center group" data-testid="navbar-logo-link">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/static/scb-logo.png`}
                alt="Seven Chapter Books" 
                className="h-16 sm:h-20 w-auto logo-hd"
                style={{ imageRendering: 'auto' }}
                loading="eager"
                decoding="sync"
                fetchPriority="high"
              />
            </Link>

            {isAuthenticated && (
              <div className="hidden md:flex items-center space-x-6">
                <Link
                  to="/"
                  onClick={handleHomeClick}
                  className="text-white hover:text-gray-300 transition-colors text-sm font-medium"
                  data-testid="nav-home"
                >
                  Home
                </Link>
                <Link
                  to="/browse"
                  className="text-white hover:text-gray-300 transition-colors text-sm font-medium"
                >
                  Browse
                </Link>
                {userRole === 'admin' && (
                  <Link
                    to="/upload"
                    className="text-white hover:text-gray-300 transition-colors text-sm font-medium flex items-center space-x-1"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Upload Book</span>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Right side */}
          {isAuthenticated ? (
            <div className="flex items-center space-x-4 pointer-events-auto">
              {/* Search */}
              <div className="relative">
                {searchOpen ? (
                  <form onSubmit={handleSearch} className="flex items-center">
                    <Input
                      type="text"
                      placeholder="Search books, authors..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-60 bg-black/80 border-white/20 text-white placeholder:text-gray-400 focus:border-white"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setSearchOpen(false)}
                      className="ml-2 text-white hover:bg-white/10"
                    >
                      ✕
                    </Button>
                  </form>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSearchOpen(true)}
                    className="text-white hover:bg-white/10"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                )}
              </div>

              {/* Notifications */}
              {/* Notification Bell */}
              <NotificationBell />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="relative text-white hover:bg-white/10 rounded-full p-0 shrink-0 inline-flex items-center justify-center transition-colors"
                    style={{ width: 36, height: 36, minWidth: 36, minHeight: 36 }}
                    data-testid="user-menu-avatar-btn"
                  >
                    <span className="rounded-full overflow-hidden inline-flex" style={{ width: 36, height: 36 }}>
                      <UserAvatar
                        name={userName}
                        avatarId={avatarId}
                        avatarPhotoUrl={avatarPhotoUrl}
                        size="md"
                        ringColor="ring-fuchsia-400/40"
                      />
                    </span>
                    {pendingApprovals > 0 && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-black"
                        data-testid="pending-approvals-avatar-badge"
                      >
                        {pendingApprovals > 9 ? '9+' : pendingApprovals}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-black/95 border-white/20">
                  <DropdownMenuItem className="text-white focus:bg-white/10 focus:text-white">
                    <div className="flex items-center gap-3 py-1">
                      <UserAvatar
                        name={userName}
                        avatarId={avatarId}
                        avatarPhotoUrl={avatarPhotoUrl}
                        size="lg"
                        testId="user-menu-header-avatar"
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">{userName}</span>
                        <span className="text-xs text-gray-400 capitalize">{userRole} Account</span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {userRole === 'admin' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate('/admin/users')}
                        className="text-white focus:bg-white/10 focus:text-white"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Manage Users
                        {pendingApprovals > 0 && (
                          <span
                            className="ml-auto bg-fuchsia-600 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                            data-testid="pending-approvals-menu-badge"
                          >
                            {pendingApprovals}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/admin/settings')}
                        className="text-white focus:bg-white/10 focus:text-white"
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Analytics & Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/upload')}
                        className="text-white focus:bg-white/10 focus:text-white"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Book
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate('/library')}
                    className="text-white focus:bg-white/10 focus:text-white"
                  >
                    <Library className="h-4 w-4 mr-2" />
                    My Library
                  </DropdownMenuItem>
                  {(userRole === 'author' || userRole === 'admin') && (
                    <DropdownMenuItem
                      onClick={() => navigate('/my-books')}
                      className="text-white focus:bg-white/10 focus:text-white"
                      data-testid="nav-my-books"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      My Books
                    </DropdownMenuItem>
                  )}
                  {(userRole === 'author' || userRole === 'admin') && (
                    <DropdownMenuItem
                      onClick={() => navigate('/me/payout-bank-details')}
                      className="text-white focus:bg-white/10 focus:text-white"
                      data-testid="nav-payout-bank-details"
                    >
                      <Banknote className="h-4 w-4 mr-2" />
                      Payout Bank Details
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate('/community')}
                    className="text-white focus:bg-white/10 focus:text-white"
                    data-testid="nav-community-chat"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Community Chat
                  </DropdownMenuItem>
                  {userRole === 'admin' && (
                    <DropdownMenuItem
                      onClick={() => navigate('/leaderboard')}
                      className="text-white focus:bg-white/10 focus:text-white"
                      data-testid="nav-leaderboard"
                    >
                      <Trophy className="h-4 w-4 mr-2" />
                      Leaderboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate('/settings')}
                    className="text-white focus:bg-white/10 focus:text-white"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-white focus:bg-white/10 focus:text-white"
                  >
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center space-x-4 pointer-events-auto">
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;